import { normalizeDoc } from './normalize.js';
import { EMPTY_DOC } from './types.js';

/**
 * Serialize a doc to a JSON string.
 * @param {object} doc
 * @returns {string}
 */
export function docToJSON(doc) {
  return JSON.stringify(normalizeDoc(doc));
}

/**
 * Parse a JSON string back to a doc object.
 * @param {string|null|undefined} json
 * @returns {object}
 */
export function docFromJSON(json) {
  if (!json) return { ...EMPTY_DOC };
  try {
    const parsed = typeof json === 'string' ? JSON.parse(json) : json;
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.root) && parsed.blocks) {
      return normalizeDoc(parsed);
    }
  } catch {
    // fall through to empty doc
  }
  return { ...EMPTY_DOC };
}

/**
 * Export a doc to an HTML string.
 * Walks the block tree and delegates to each block's serialize function.
 * Falls back to empty string for unknown block types.
 *
 * @param {object} doc
 * @param {object} registry  – pass getAllBlockDefinitions() result keyed by type
 * @returns {string}
 */
export function docToHTML(doc, registry = {}) {
  if (!doc || !doc.root) return '';
  const renderList = (ids) =>
    ids
      .map((id) => renderBlock(id))
      .join('');

  const renderBlock = (id) => {
    const block = doc.blocks[id];
    if (!block) return '';
    const def = registry[block.type];
    if (def && typeof def.serialize === 'function') {
      const innerHtml = block.children ? renderList(block.children) : '';
      return def.serialize(block.attrs, innerHtml, block.children || null);
    }
    // generic fallback
    if (block.type === 'text' || block.type === 'heading') {
      return block.attrs.html || block.attrs.text || '';
    }
    if (Array.isArray(block.children)) {
      return renderList(block.children);
    }
    return '';
  };

  return renderList(doc.root);
}

/**
 * Attempt to migrate a legacy content block (the old string-based format) into
 * a doc object so the new editor can open it.
 *
 * @param {{ contentType: string, content: string }} legacy
 * @returns {object} doc
 */
export function migrateLegacyContent(legacy) {
  if (!legacy) return { ...EMPTY_DOC };
  const { contentType, content } = legacy;

  if (!content) return { ...EMPTY_DOC };

  if (contentType === 'text' || contentType === 'html' || !contentType) {
    const id = `block-migrated-text`;
    return {
      root: [id],
      blocks: {
        [id]: { id, type: 'text', attrs: { html: content } },
      },
    };
  }

  if (contentType === 'image') {
    const id = `block-migrated-image`;
    return {
      root: [id],
      blocks: {
        [id]: { id, type: 'image', attrs: { url: content, alt: '' } },
      },
    };
  }

  if (contentType === 'video') {
    const id = `block-migrated-video`;
    return {
      root: [id],
      blocks: {
        [id]: { id, type: 'image', attrs: { url: content, alt: '', isVideo: true } },
      },
    };
  }

  return { ...EMPTY_DOC };
}
